import { Field } from 'module/api/field/types';
import { unreachable } from 'module/helpers/unreachable';
import { FieldType } from 'module/types/filter-field/field/field';
import { humanise } from 'module/ui/components/atoms/humanised-date';
import { formatter } from 'module/ui/components/atoms/input-duration/formatter';
import { placeholderForEmpty } from 'module/ui/components/atoms/no-value/empty-cell';
import { ValueFormatterParams } from 'ag-grid-community';
import { memoize } from 'lodash';
import moment from 'moment';

type Nullable<T> = T | null;

interface ValueFormatter {
  (params: ValueFormatterParams): string;
}

const formatNumber = memoize((value: Nullable<number>) =>
  value == null ? placeholderForEmpty : value.toString(),
);

export const formatEffortAsDuration = memoize((value: Nullable<number>) =>
  value == null ? placeholderForEmpty : `${formatter(value)}`,
);

export const formatDate = memoize((value: Nullable<number>) =>
  value == null ? placeholderForEmpty : humanise(moment(value)),
);

export const formatDateUTC = memoize((value: Nullable<number>) =>
  value == null ? placeholderForEmpty : humanise(moment.utc(value)),
);
const formatText = memoize((value: Nullable<string>) =>
  value == null ? placeholderForEmpty : value,
);

type NewField = Field & {
  type: FieldType;
};

export const getValueFormatter = (
  field: Pick<NewField, 'type' | 'fieldName'>,
): ValueFormatter | undefined => {
  const { type } = field;

  switch (type) {
    case FieldType.Number:
      return ({ value }: { value: Nullable<number> }) => formatNumber(value);
    case FieldType.Date:
      return ({ value }: { value: Nullable<number> }) => formatDate(value);
    case FieldType.Text:
    case FieldType.MultiLineText:
      return ({ value }: { value: Nullable<string> }) => formatText(value);
    case FieldType.Boolean:
    case FieldType.SingleSelect:
    case FieldType.MultiSelect:
    case FieldType.SingleUser:
    case FieldType.MultiUser:
    case FieldType.Assignee:
    case FieldType.Note:
    case FieldType.Estimate: // estimate is not an actual custom field type. In future if it is needed, please handle the case accordingly.
      return undefined;
    default:
      return unreachable(type);
  }
};
